import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToSection } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useFestivalTheme } from "@/context/FestivalThemeContext";

// to = route, section = landing-page anchor (works from any page)
const columns = (categories) => [
  {
    title: "Shop PAQVERO",
    links: [
      { label: "Shop All", to: "/shop" },
      { label: "Bulk Deals", to: "/shop?bulk=1" },
      { label: "Track Your Order", to: "/track" },
      { label: "PAQVERO Credit", to: "/account/credit" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About PAQVERO", section: "#about" },
      { label: "Careers", to: "/careers" },
      { label: "Newsroom", to: "/newsroom" },
      { label: "Contact", to: "/contact" },
    ],
  },
  ...(categories.length
    ? [{
        title: "Products",
        links: categories.map((c) => ({
          label: c.name,
          to: `/shop?category=${encodeURIComponent(c.name)}`,
        })),
      }]
    : []),
  {
    title: "Support",
    links: [
      { label: "Help Center", to: "/help" },
      { label: "Shipping & Returns", to: "/shipping-returns" },
      { label: "FAQs", to: "/faqs" },
      { label: "Track an Order", to: "/track" },
    ],
  },
];

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const { content: themeContent } = useFestivalTheme();
  // ink footer always carries the white wordmark
  const logoDark = themeContent.logo_dark || "/paqvero-logo.png";

  useEffect(() => {
    api.get("/shop/categories")
      .then(({ data }) => setCategories((data.categories || data || []).slice(0, 4)))
      .catch(() => setCategories([]));
  }, []);

  const go = (link) => {
    if (link.to) {
      navigate(link.to);
      return;
    }
    // landing-page section — jump directly or hand off via route state (mirrors Navbar)
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: link.section } });
    } else {
      scrollToSection(link.section);
    }
  };

  return (
    <footer data-testid="footer" className="bg-[#20221F]">
      <div className="mx-auto max-w-7xl px-6 pb-28 pt-16 md:pb-16 lg:px-12 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <button
              data-testid="footer-logo"
              aria-label="Back to top"
              onClick={() => (location.pathname !== "/" ? navigate("/") : scrollToSection("#top"))}
              className="flex items-center transition-transform duration-300 hover:scale-[1.03]"
            >
              <img src={logoDark} alt="PAQVERO" width="125" height="30" className="h-[30px] w-auto" />
            </button>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-[#A5A49A]">
              Business supplies, sourced directly from manufacturers and stocked
              by PAQVERO. Order in minutes — we pick, pack and deliver to your door.
            </p>
            <p className="mt-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#A5A49A]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#287A55]" />
              Churchgate–Borivali corridor · Mumbai
            </p>
          </div>

          {columns(categories).map((col) => (
            <div key={col.title}>
              <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E4B66D]">
                {col.title}
              </h4>
              <ul className="mt-5 space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <button
                      onClick={() => go(link)}
                      data-testid={`footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      className="text-left text-sm text-[#C9C7BC] transition-colors duration-300 hover:text-[#E4B66D]"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <p className="text-xs text-[#77776F]">
            © 2026 PAQVERO Supplies Pvt. Ltd. All rights reserved.
          </p>
          <div className="flex gap-6">
            <button onClick={() => navigate("/privacy")} data-testid="footer-privacy" className="text-xs text-[#A5A49A] transition-colors hover:text-[#E4B66D]">
              Privacy
            </button>
            <button onClick={() => navigate("/terms")} data-testid="footer-terms" className="text-xs text-[#A5A49A] transition-colors hover:text-[#E4B66D]">
              Terms
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
